import React from "react";
import { render, screen, fireEvent, act } from "@testing-library/react";
import "@testing-library/jest-dom";

import { FullPageChatLayout } from "./FullPageChatLayout";

// WHAT THESE TESTS PROTECT, AND WHAT THEY DO NOT.
//
// They assert the follow effect's DECISION against browser behaviour captured
// as constants. The constants come from a probe run in Chrome against the real
// IDA scroll container (recorded in the PR body): appended content below
// leaves scrollTop untouched; content growing ABOVE moves scrollTop DOWN by
// the grown height; content collapsing ABOVE moves it UP by the collapsed
// height; only a reader moves it without the content moving with it.
//
// So these tests protect the decision, NOT the premise. If Chrome changes how
// it adjusts a scroll offset, every test here stays green while the assumption
// underneath has moved. Re-run the browser probe, do not trust this file alone.

jest.mock("@metalmind-ab/mmagent-chat-ui", () => ({
  ChatMessage: ({ message }: { message: any }) => (
    <div data-testid={`chat-message-${message.id}`}>{message.content}</div>
  ),
  JobProgressIndicator: () => null,
  useChatStore: (selector: (state: any) => any) =>
    selector({
      backgroundJobs: [],
      removeBackgroundJob: jest.fn(),
      messages: [],
    }),
}));

beforeAll(() => {
  Object.defineProperty(HTMLElement.prototype, "scrollIntoView", {
    configurable: true,
    value: jest.fn(),
  });
  Object.defineProperty(URL, "createObjectURL", {
    configurable: true,
    value: jest.fn(() => "blob:test-url"),
  });
  Object.defineProperty(URL, "revokeObjectURL", {
    configurable: true,
    value: jest.fn(),
  });
  // jsdom reports 0 for every layout property. Back offsetTop with a value the
  // test sets per element, so content moving ABOVE an element can be modelled.
  Object.defineProperty(HTMLElement.prototype, "offsetTop", {
    configurable: true,
    get(this: any) {
      return this.__offsetTop ?? 0;
    },
  });
});

// Must match SCROLL_PIN_THRESHOLD_PX in the component.
const PIN_THRESHOLD_PX = 80;

const messages = [
  {
    id: "m1",
    role: "assistant",
    content: "Reply",
    createdAt: new Date("2026-04-25T10:00:00Z"),
  },
];

const layout = (overrides: Record<string, unknown> = {}) => (
  <FullPageChatLayout
    messages={messages as any}
    onSendMessage={jest.fn()}
    isLoading={false}
    conversations={[] as any}
    onLoadConversation={jest.fn()}
    onNewConversation={jest.fn()}
    onSearchConversations={jest.fn()}
    onLoadMoreConversations={jest.fn()}
    conversationsLoading={false}
    currentConversationId={1}
    {...(overrides as any)}
  />
);

/**
 * jsdom has no layout, so the scroll container's geometry is simulated. The
 * scrollTop setter CLAMPS, because the browser clamps an assignment to the
 * maximum offset and the code under test reads the value back precisely for
 * that reason -- without clamping here the read-back would go untested.
 */
function instrument(
  container: HTMLElement,
  initial: { scrollHeight: number; clientHeight: number; scrollTop: number },
) {
  const geom = { ...initial };
  const writes: number[] = [];
  Object.defineProperty(container, "scrollHeight", {
    configurable: true,
    get: () => geom.scrollHeight,
  });
  Object.defineProperty(container, "clientHeight", {
    configurable: true,
    get: () => geom.clientHeight,
  });
  Object.defineProperty(container, "scrollTop", {
    configurable: true,
    get: () => geom.scrollTop,
    set: (v: number) => {
      writes.push(v);
      geom.scrollTop = Math.max(
        0,
        Math.min(v, geom.scrollHeight - geom.clientHeight),
      );
    },
  });
  return {
    geom,
    writes,
    distance: () => geom.scrollHeight - geom.clientHeight - geom.scrollTop,
  };
}

/** Set up a reader pinned at the bottom, with the effect baselined. */
function pinnedAtBottom() {
  const view = render(layout());
  const container = screen.getByTestId("chat-scroll-container");
  // The component deliberately uses the message row as its content anchor.
  // eslint-disable-next-line testing-library/no-node-access
  const anchor = screen.getByTestId("chat-message-m1").parentElement!;
  const h = instrument(container, {
    scrollHeight: 10000,
    clientHeight: 1118,
    scrollTop: 0,
  });
  (anchor as any).__offsetTop = 9000;

  // The container is only discoverable once there is content to scroll, so the
  // first commit finds nothing. This commit is the one that baselines.
  view.rerender(layout({ messages: [...messages] }));
  const commit = () => {
    h.writes.length = 0;
    view.rerender(layout({ messages: [...messages] }));
  };
  return { view, container, anchor, h, commit };
}

// The content causes, as measured in Chrome. Each moves the offset, the
// content height, and the anchor's position together in a fixed pattern.
// A scroll event fires if and ONLY IF the offset actually moved. Measured on
// the real container: a collapse moves the offset and DOES dispatch one (with
// distance 0, because the container is then clamped to the bottom); content
// appended below leaves the offset untouched and dispatches NOTHING. That
// asymmetry is not incidental -- it is why the inflated distance that fools a
// commit-time sampler is never visible at the instant of a scroll -- so the
// model has to reproduce it or it is testing a browser that does not exist.
const appendBelow = (h: any, px: number) => {
  h.geom.scrollHeight += px; // offset unchanged -> no scroll event
};
const growAbove = (
  h: any,
  anchor: HTMLElement,
  px: number,
  container: HTMLElement,
) => {
  h.geom.scrollTop += px;
  h.geom.scrollHeight += px;
  (anchor as any).__offsetTop += px;
  fireEvent.scroll(container);
};
const collapseAbove = (
  h: any,
  anchor: HTMLElement,
  px: number,
  container: HTMLElement,
) => {
  h.geom.scrollTop -= px;
  h.geom.scrollHeight -= px;
  (anchor as any).__offsetTop -= px;
  fireEvent.scroll(container);
};
// A card inside the LAST message: its height shrinks, its top does not move,
// and the clamp drags the offset up anyway.
const collapseInsideAnchor = (
  h: any,
  px: number,
  container: HTMLElement,
) => {
  h.geom.scrollTop -= px;
  h.geom.scrollHeight -= px;
  fireEvent.scroll(container);
};
// Deliberately dispatches NOTHING: the undelivered scroll event is the race.
const readerScrollsUp = (h: any, px: number) => {
  h.geom.scrollTop -= px;
};

describe("FullPageChatLayout stream following", () => {
  it("COMPOSITE: keeps following when content above collapses and a chunk appends below", () => {
    const { container, anchor, h, commit } = pinnedAtBottom();
    expect(h.distance()).toBe(0);

    // The reader clicks a collapse toggle on a tool card. That is a pointerdown
    // on CONTENT: it cannot scroll this container, but it does arm intent.
    fireEvent.pointerDown(container);

    // The card is above the viewport, so collapsing it moves the offset UP...
    collapseAbove(h, anchor, 800, container);
    // ...and the stream appends the next chunk BELOW before the commit lands.
    appendBelow(h, 200);

    // THE INSTRUMENT. Without this the test cannot tell "the guard held" from
    // "distance could not have moved". Pinned at the bottom, a collapse alone
    // leaves distance clamped at 0 and the assertion below is vacuous.
    expect(h.distance()).toBeGreaterThan(PIN_THRESHOLD_PX);

    commit();

    expect(h.writes.length).toBeGreaterThan(0);
    expect(h.distance()).toBe(0);
  });

  it("A: keeps a reader's scroll that arrived before its scroll event was delivered", () => {
    const { container, h, commit } = pinnedAtBottom();
    const before = h.geom.scrollTop;

    // A wheel notch. The scroll event it causes is deliberately NOT dispatched:
    // that undelivered event is the race, and a commit lands inside the gap.
    fireEvent.wheel(container);
    readerScrollsUp(h, 600);

    commit();

    expect(h.writes).toHaveLength(0);
    expect(h.geom.scrollTop).toBe(before - 600);
  });

  it("B: follows appended content across a burst without unpinning", () => {
    const { h, commit } = pinnedAtBottom();

    for (let i = 0; i < 20; i += 1) {
      appendBelow(h, 40);
      commit();
      expect(h.writes.length).toBeGreaterThan(0);
      expect(h.distance()).toBe(0);
    }
  });

  it("C: keeps following through growth then collapse above, with distance not saturated", () => {
    const { container, anchor, h, commit } = pinnedAtBottom();

    // Append first, so distance is a number the guard could act on. Without
    // this the container sits clamped at maximum scroll and distance is pinned
    // to 0 whatever the guard does -- which is why the original version of this
    // test would have passed against code with the guard deleted.
    appendBelow(h, 200);
    growAbove(h, anchor, 700, container);
    collapseAbove(h, anchor, 700, container);
    fireEvent.pointerDown(container);

    // THE INSTRUMENT.
    expect(h.distance()).toBeGreaterThan(PIN_THRESHOLD_PX);

    commit();

    expect(h.writes.length).toBeGreaterThan(0);
    expect(h.distance()).toBe(0);
  });
});

describe("FullPageChatLayout bounded message history", () => {
  it("offers an explicit older-page action above a loaded message window", () => {
    const onLoadOlderMessages = jest.fn();
    render(
      layout({
        hasOlderMessages: true,
        onLoadOlderMessages,
      }),
    );

    fireEvent.click(
      screen.getByRole("button", { name: "Load earlier messages" }),
    );

    expect(onLoadOlderMessages).toHaveBeenCalledTimes(1);
  });

  it("keeps older-page recovery reachable when a partial cluster maps to no messages", () => {
    render(
      layout({
        messages: [],
        hasOlderMessages: true,
        onLoadOlderMessages: jest.fn(),
      }),
    );

    expect(
      screen.getByRole("button", { name: "Load earlier messages" }),
    ).toBeInTheDocument();
  });
});

// ADVERSARIAL: the anchor is the LAST message, and the message that is
// streaming is the last message. A tool card INSIDE it is therefore BELOW the
// anchor's top, so collapsing it does not move the anchor's offsetTop -- but
// the container is clamped at the bottom, so scrollTop still moves up by the
// collapsed height. That is the same shape as the composite with the anchor
// blind to it.
describe("FullPageChatLayout stream following - adversarial", () => {
  it("keeps following when a card INSIDE the last message collapses and a chunk appends below", () => {
    const view = render(layout());
    const container = screen.getByTestId("chat-scroll-container");
    // The component deliberately uses the message row as its content anchor.
    // eslint-disable-next-line testing-library/no-node-access
    const anchor = screen.getByTestId("chat-message-m1").parentElement!;
    const h = instrument(container, {
      scrollHeight: 10000,
      clientHeight: 1118,
      scrollTop: 0,
    });
    (anchor as any).__offsetTop = 9000;
    view.rerender(layout({ messages: [...messages] }));
    expect(h.distance()).toBe(0);

    fireEvent.pointerDown(container);

    collapseInsideAnchor(h, 800, container);
    appendBelow(h, 200);

    expect(h.distance()).toBeGreaterThan(PIN_THRESHOLD_PX);

    h.writes.length = 0;
    view.rerender(layout({ messages: [...messages] }));

    expect(h.writes.length).toBeGreaterThan(0);
    expect(h.distance()).toBe(0);
  });
});

// WHY NO SINGLE-SAMPLE-PER-COMMIT RULE CAN WORK.
//
// Two histories. In one the reader deliberately scrolled up 800 and following
// must STOP. In the other nobody touched anything -- a card inside the last
// message collapsed and the stream appended -- and following must CONTINUE.
// At commit time every quantity the effect can read is IDENTICAL. Any rule
// computed from one sample must give both the same answer, so it is wrong
// about one of them, whatever the rule is. This is not a defect of a
// particular threshold; it is the reason the question itself is unanswerable
// from a single sample, and why the fix has to change the QUESTION rather than
// the arithmetic.
describe("single-sample indistinguishability", () => {
  it("a reader scrolling up and a collapse-plus-append are the same observation", () => {
    const C = 1118;
    const start = { scrollTop: 8882, scrollHeight: 10000, anchorOffsetTop: 9000 };
    const clamp = (top: number, height: number) =>
      Math.max(0, Math.min(top, height - C));

    // History 1: the reader scrolls up 800, then a 200px chunk appends below.
    const a = { ...start };
    a.scrollTop -= 800;
    a.scrollHeight += 200;

    // History 2: a card INSIDE the last message collapses by 800 (the anchor's
    // top does not move, but the clamp drags the offset up), then 1000px
    // appends below.
    const b = { ...start };
    b.scrollHeight -= 800;
    b.scrollTop = clamp(b.scrollTop, b.scrollHeight);
    b.scrollHeight += 1000;

    expect(b).toEqual(a);
    // ...and the distance both rules would read is the same number too.
    expect(a.scrollHeight - C - a.scrollTop).toBe(
      b.scrollHeight - C - b.scrollTop,
    );
  });
});

// The two cases the design is most likely to get wrong, constructed rather
// than reasoned about. Both let the suppression window EXPIRE first, so the
// behaviour under test is the steady state and not the window.
const settle = () =>
  act(async () => {
    await new Promise((r) => setTimeout(r, 60));
  });

describe("FullPageChatLayout stream following - steady state", () => {
  it("stays put across a long burst after the reader stops, not at the bottom", async () => {
    const view = render(layout());
    const container = screen.getByTestId("chat-scroll-container");
    const h = instrument(container, {
      scrollHeight: 10000,
      clientHeight: 1118,
      scrollTop: 0,
    });
    view.rerender(layout({ messages: [...messages] }));

    // the reader scrolls up and their scroll event IS delivered
    fireEvent.wheel(container);
    h.geom.scrollTop -= 4000;
    fireEvent.scroll(container);
    await settle(); // suppression window expires; last input is long past

    h.writes.length = 0;
    for (let i = 0; i < 40; i += 1) {
      appendBelow(h, 60);
      view.rerender(layout({ messages: [...messages] }));
    }

    expect(h.writes).toHaveLength(0);
    expect(h.geom.scrollTop).toBe(8882 - 4000);
  });

  it("re-pins and resumes following when the reader returns to the bottom mid-stream", async () => {
    const view = render(layout());
    const container = screen.getByTestId("chat-scroll-container");
    const h = instrument(container, {
      scrollHeight: 10000,
      clientHeight: 1118,
      scrollTop: 0,
    });
    view.rerender(layout({ messages: [...messages] }));

    fireEvent.wheel(container);
    h.geom.scrollTop -= 4000;
    fireEvent.scroll(container);
    await settle();

    h.writes.length = 0;
    appendBelow(h, 60);
    view.rerender(layout({ messages: [...messages] }));
    expect(h.writes).toHaveLength(0); // unpinned, as expected

    // the reader scrolls back down to the bottom, with the stream still running
    fireEvent.wheel(container);
    h.geom.scrollTop = h.geom.scrollHeight - h.geom.clientHeight;
    fireEvent.scroll(container);
    await settle();

    h.writes.length = 0;
    appendBelow(h, 60);
    view.rerender(layout({ messages: [...messages] }));

    expect(h.writes.length).toBeGreaterThan(0); // following again, no second gesture
    expect(h.distance()).toBe(0);
  });
});
